<script setup>
import { computed } from 'vue';

import { resolveFlowText } from './flowJsonSchema';

const props = defineProps({
  node: { type: Object, required: true },
  values: { type: Object, required: true },
});

const emit = defineEmits(['action', 'update']);

defineOptions({ name: 'FlowJsonNode' });

const text = computed(() =>
  resolveFlowText(props.node.text || '', props.values)
);
const options = computed(
  () => props.node['data-source'] || props.node.options || []
);
const inputValue = computed({
  get: () => props.values[props.node.name] ?? '',
  set: value => emit('update', { name: props.node.name, value }),
});
const isVisible = computed(() => props.node.visible !== false);
const normalizedType = computed(() => props.node.type || 'Unknown');
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div v-if="isVisible" class="space-y-2">
    <h2
      v-if="normalizedType === 'TextHeading'"
      class="text-lg font-semibold text-slate-900"
    >
      {{ text }}
    </h2>
    <h3
      v-else-if="normalizedType === 'TextSubheading'"
      class="text-base font-semibold text-slate-900"
    >
      {{ text }}
    </h3>
    <p
      v-else-if="normalizedType === 'TextBody'"
      class="text-sm whitespace-pre-line text-slate-700"
    >
      {{ text }}
    </p>
    <p
      v-else-if="normalizedType === 'TextCaption'"
      class="text-xs whitespace-pre-line text-slate-500"
    >
      {{ text }}
    </p>
    <div
      v-else-if="normalizedType === 'RichText'"
      class="text-sm whitespace-pre-line text-slate-700"
    >
      {{ Array.isArray(node.text) ? node.text.join('\n') : text }}
    </div>

    <label
      v-else-if="normalizedType === 'TextInput'"
      class="block text-xs font-medium text-slate-700"
    >
      {{ node.label || node.name }}
      <input
        v-model="inputValue"
        :type="node['input-type'] === 'email' ? 'email' : 'text'"
        :placeholder="node['helper-text'] || ''"
        class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-emerald-600"
      />
    </label>
    <label
      v-else-if="normalizedType === 'TextArea'"
      class="block text-xs font-medium text-slate-700"
    >
      {{ node.label || node.name }}
      <textarea
        v-model="inputValue"
        :placeholder="node['helper-text'] || ''"
        class="mt-1 min-h-20 w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-emerald-600"
      />
    </label>
    <label
      v-else-if="normalizedType === 'Dropdown'"
      class="block text-xs font-medium text-slate-700"
    >
      {{ node.label || node.name }}
      <select
        v-model="inputValue"
        class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
      >
        <option value="" disabled>
          {{ $t('COMBOBOX.PLACEHOLDER') }}
        </option>
        <option v-for="option in options" :key="option.id" :value="option.id">
          {{ option.title }}
        </option>
      </select>
    </label>
    <fieldset
      v-else-if="normalizedType === 'RadioButtonsGroup'"
      class="space-y-2"
    >
      <legend class="text-xs font-medium text-slate-700">
        {{ node.label || node.name }}
      </legend>
      <label
        v-for="option in options"
        :key="option.id"
        class="flex items-center gap-2 rounded-lg border border-slate-200 p-2 text-sm text-slate-700"
      >
        <input v-model="inputValue" type="radio" :value="option.id" />
        {{ option.title }}
      </label>
    </fieldset>
    <fieldset v-else-if="normalizedType === 'CheckboxGroup'" class="space-y-2">
      <legend class="text-xs font-medium text-slate-700">
        {{ node.label || node.name }}
      </legend>
      <label
        v-for="option in options"
        :key="option.id"
        class="flex items-center gap-2 rounded-lg border border-slate-200 p-2 text-sm text-slate-700"
      >
        <input
          :checked="(values[node.name] || []).includes(option.id)"
          type="checkbox"
          @change="
            emit('update', {
              name: node.name,
              value: $event.target.checked
                ? [...(values[node.name] || []), option.id]
                : (values[node.name] || []).filter(id => id !== option.id),
            })
          "
        />
        {{ option.title }}
      </label>
    </fieldset>
    <label
      v-else-if="normalizedType === 'OptIn'"
      class="flex items-start gap-2 text-sm text-slate-700"
    >
      <input v-model="inputValue" type="checkbox" class="mt-1" />
      {{ node.label || text }}
    </label>
    <label
      v-else-if="['DatePicker', 'CalendarPicker'].includes(normalizedType)"
      class="block text-xs font-medium text-slate-700"
    >
      {{ node.label || node.name }}
      <input
        v-model="inputValue"
        type="date"
        class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
      />
    </label>
    <img
      v-else-if="normalizedType === 'Image' && node.src"
      :src="node.src"
      :alt="node['alt-text'] || ''"
      class="max-h-40 w-full rounded-lg object-cover"
    />
    <a
      v-else-if="normalizedType === 'EmbeddedLink'"
      :href="node['on-click-action']?.url || '#'"
      class="text-sm font-medium text-emerald-700 underline"
      target="_blank"
      rel="noopener noreferrer"
    >
      {{ text }}
    </a>
    <button
      v-else-if="normalizedType === 'Footer'"
      type="button"
      class="w-full rounded-lg bg-[#00a884] px-3 py-2.5 text-sm font-semibold text-white"
      @click="emit('action', node['on-click-action'] || {})"
    >
      {{ node.label || 'Continuar' }}
    </button>
    <div
      v-else-if="['Form', 'If', 'Switch'].includes(normalizedType)"
      class="space-y-3"
    >
      <FlowJsonNode
        v-for="(child, index) in node.children || []"
        :key="child.name || `${child.type}-${index}`"
        :node="child"
        :values="values"
        @action="emit('action', $event)"
        @update="emit('update', $event)"
      />
    </div>
    <div
      v-else
      class="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-2 text-xs text-slate-500"
    >
      {{ node.label || node.type || 'Componente do Flow' }}
    </div>
  </div>
  <div v-else />
  <!-- eslint-enable vue/no-bare-strings-in-template -->
</template>
